import { Link, useNavigate } from "react-router-dom";
import { useLiveQuery } from "dexie-react-hooks";
import { db } from "../schema.dexie";
import { filaReexposicao } from "../domain";
import {
  confirmarOferta,
  descartarOferta,
  getConfig,
  getCriancaAtiva,
  getPendentes,
} from "../db/queries";
import { PASSO_ESCADA_LABEL } from "../types";
import { diasAtras } from "../lib/format";
import { FoodPhoto } from "../ui/FoodPhoto";
import { IndicadorEvolucao } from "../ui/IndicadorEvolucao";

export function Hoje() {
  const nav = useNavigate();
  const crianca = useLiveQuery(getCriancaAtiva);
  const cfg = useLiveQuery(getConfig);
  const alimentos = useLiveQuery(() => db.alimento.toArray()) ?? [];
  const ofertas = useLiveQuery(() => db.oferta.toArray()) ?? [];
  const pendentes = useLiveQuery(getPendentes) ?? [];

  const cadencia = cfg?.reexposicao_dias ?? 5;
  const reofertar = filaReexposicao(alimentos, ofertas, cadencia);

  const ativos = alimentos.filter((a) => !a.arquivado);
  const nSeguros = ativos.filter((a) => a.status === "seguro").length;
  const nTeste = ativos.filter((a) => a.status === "em_teste").length;
  const nExperimentar = ativos.filter((a) => a.status === "nao_oferecido").length;

  const nomeDe = (id: string) =>
    alimentos.find((a) => a.id === id)?.nome ?? "alimento";

  return (
    <main className="tela">
      <div className="row-entre">
        <div>
          <h1 className="tela-titulo">olá{crianca ? `, ${crianca.apelido}` : ""}</h1>
        </div>
        <Link to="/config" className="btn btn-fantasma" aria-label="configurações">
          ⚙️
        </Link>
      </div>

      {!crianca && (
        <button
          className="card linha-alimento"
          style={{ width: "100%", marginBottom: "var(--s4)" }}
          onClick={() => nav("/paciente")}
        >
          <div className="foto" aria-hidden>
            👤
          </div>
          <div className="crescer">
            <div className="nome">complete o cadastro do paciente</div>
            <div className="meta">nome, nascimento e responsável</div>
          </div>
          <span aria-hidden>→</span>
        </button>
      )}

      {/* onboarding curto — some assim que houver alimentos */}
      {ativos.length === 0 && (
        <div className="card boas-vindas">
          <div className="bv-titulo">bem-vindo(a) 🌱</div>
          <p className="bv-sub">como funciona, em 3 passos:</p>
          <ol className="bv-passos">
            <li>
              <strong>cadastre alimentos</strong> na <Link to="/biblioteca">biblioteca</Link>{" "}
              (ou toque os populares ao preparar a refeição).
            </li>
            <li>
              <strong>prepare a refeição</strong>: o app sugere a base e você monta o prato.
            </li>
            <li>
              a <strong>criança escolhe</strong> e você gera o pedido em PDF/WhatsApp.
            </li>
          </ol>
          <p className="bv-rodape">tudo fica neste aparelho, sem conta.</p>
        </div>
      )}

      <button
        className="btn btn-primario btn-bloco btn-grande"
        style={{ marginBottom: "var(--s5)" }}
        onClick={() => nav("/registrar")}
      >
        ➕ registrar recomendação
      </button>

      {/* panorama do repertório — ver evolução de relance */}
      {ativos.length > 0 && (
        <section className="panorama">
          <Link to="/seguros" className="stat stat-seguro">
            <span className="stat-num">{nSeguros}</span>
            <span className="stat-rot">seguros</span>
          </Link>
          <Link to="/biblioteca?status=em_teste" className="stat stat-teste">
            <span className="stat-num">{nTeste}</span>
            <span className="stat-rot">em teste</span>
          </Link>
          <Link to="/biblioteca?status=nao_oferecido" className="stat">
            <span className="stat-num">{nExperimentar}</span>
            <span className="stat-rot">a experimentar</span>
          </Link>
        </section>
      )}

      {/* entrada do modo criança — logo abaixo do panorama */}
      <button
        className="btn btn-bloco btn-grande"
        onClick={() => nav("/refeicao")}
        style={{ marginBottom: "var(--s5)" }}
      >
        🍽️ preparar refeição da criança
      </button>

      {/* P0.3 — confirmar escolhas da criança (pendentes) */}
      {pendentes.length > 0 && (
        <section className="secao">
          <h2 className="secao-titulo">escolhas da criança pra confirmar</h2>
          {pendentes.map((o) => (
            <div className="card" key={o.id}>
              <div className="row-entre">
                <div className="row">
                  <FoodPhoto
                    fotoId={alimentos.find((a) => a.id === o.alimento_id)?.fotos[0]}
                    nome={nomeDe(o.alimento_id)}
                    categoria={alimentos.find((a) => a.id === o.alimento_id)?.categoria}
                  />
                  <div>
                    <div style={{ fontWeight: 500 }}>{nomeDe(o.alimento_id)}</div>
                    <div className="meta" style={{ color: "var(--text-muted)" }}>
                      escolheu · {PASSO_ESCADA_LABEL[o.passo_escada]}
                    </div>
                  </div>
                </div>
              </div>
              <div className="row" style={{ marginTop: "var(--s3)" }}>
                <button
                  className="btn btn-primario crescer"
                  onClick={() => confirmarOferta(o.id)}
                >
                  confirmar
                </button>
                <button className="btn" onClick={() => descartarOferta(o.id)}>
                  descartar
                </button>
              </div>
            </div>
          ))}
        </section>
      )}

      {/* P0.6 — lembrete de re-exposição espaçada */}
      <section className="secao">
        <h2 className="secao-titulo">que tal reoferecer?</h2>
        {reofertar.length === 0 ? (
          <div className="card">
            <span style={{ color: "var(--text-muted)" }}>
              nada pendente — tudo em dia 🌱
            </span>
          </div>
        ) : (
          reofertar.slice(0, 5).map(({ alimento, dias_desde_ultima }) => (
            <button
              className="card linha-alimento"
              key={alimento.id}
              onClick={() => nav(`/registrar/${alimento.id}`)}
            >
              <FoodPhoto fotoId={alimento.fotos[0]} nome={alimento.nome} categoria={alimento.categoria} />
              <div className="crescer">
                <div className="nome">{alimento.nome}</div>
                <div className="meta">{diasAtras(dias_desde_ultima)}</div>
                <IndicadorEvolucao alimentoId={alimento.id} ofertas={ofertas} />
              </div>
              <span aria-hidden>➕</span>
            </button>
          ))
        )}
      </section>

    </main>
  );
}
